import { Container, Graphics, Sprite } from 'pixi.js';
import { type AlleyExtra, pathLook } from '../../data/full/festival';
import {
  GATE,
  type Way,
  alleyExtras,
  alleyPlace,
  alongOf,
  footingOf,
  gateSpot,
  headingOf,
  trailExtras,
  wayOf,
} from '../../data/full/path-layout';
import type { DecorId } from '../../data/types';
import { nextFloat, seedRng } from '../../shared/prng';
import { clamp, frac } from '../../shared/scalar';
import { TICK_RATE_HZ } from '../../shared/tempo';
import type { PlayerState, SimState } from '../../sim/state';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import type { Ground } from '../ground';
import { type Layout, OVERFLOW } from '../ground-layout';
import { releaseDrawing } from '../util';
import { drawGate, gateLights } from './exit-looks';
import { drawBunting, drawPlanks } from './path-alley';
import { ALLEY_DRAWINGS, type StandColors } from './path-stands';
import { type Wake, createWake, stepWake, wakeFlies } from './path-life';
import { drawDirt, drawShores, drawStoneLanterns } from './path-trail';
import { TRAIL_DRAWINGS } from './path-trail-extras';
import { drawTreeLine } from './path-trees';
import { FLOWERS, PATH_COLORS, towardTrail, turnOf } from './path-way';

const TUFT_AREA = 3000;
const FLOWER_AREA = 90_000;
const BULB_GLOW = 16;
const FLY_GLOW = 5;
// A late frame moves the wake no further than this.
const MAX_WAKE_STEP_TICKS = 8;
const EXTRA_GLOW = 60;
const LAMP_GLOW = 46;
const LAKE_FLY_GLOW = 12;
const UNDER_ALPHA = 0.35;

interface Roof {
  readonly drawing: Graphics;
  readonly box: { minX: number; minY: number; maxX: number; maxY: number };
}

function covers({ drawing, box }: Roof, { x, y }: PlayerState): boolean {
  const dx = x - drawing.x;
  const dy = y - drawing.y;
  const [cos, sin] = [Math.cos(drawing.rotation), Math.sin(drawing.rotation)];
  const localX = dx * cos + dy * sin;
  const localY = dy * cos - dx * sin;
  return localX >= box.minX && localX <= box.maxX && localY >= box.minY && localY <= box.maxY;
}

export function createPathGround(parent: Container): Ground {
  const root = new Container();
  const surface = new Graphics();
  const tufts = new Graphics();
  const flowers = new Graphics();
  root.addChild(surface, tufts, flowers);
  parent.addChild(root);

  return {
    root,
    draw(layout: Layout, seed: number): void {
      const { width, height } = layout.arena;
      const rng = seedRng(seed >>> 0);
      const left = -OVERFLOW;
      const top = -OVERFLOW;
      const wide = width + 2 * OVERFLOW;
      const high = height + 2 * OVERFLOW;
      surface.clear().rect(left, top, wide, high).fill(PATH_COLORS.meadow);
      tufts.clear();
      for (let i = (wide * high) / TUFT_AREA; i > 0; i--) {
        const x = left + nextFloat(rng) * wide;
        const y = top + nextFloat(rng) * high;
        const lean = (nextFloat(rng) - 0.5) * 8;
        tufts
          .moveTo(x, y)
          .lineTo(x + lean - 4, y - 12)
          .moveTo(x, y)
          .lineTo(x + lean, y - 15)
          .moveTo(x, y)
          .lineTo(x + lean + 4, y - 11);
      }
      tufts.stroke({ width: 2, color: PATH_COLORS.tuft, alpha: 0.7, cap: 'round' });
      flowers.clear();
      for (let i = Math.floor((wide * high) / FLOWER_AREA); i > 0; i--) {
        const x = left + nextFloat(rng) * wide;
        const y = top + nextFloat(rng) * high;
        for (let petal = 0; petal < 7; petal++) {
          flowers.circle(
            x + (nextFloat(rng) - 0.5) * 50,
            y + (nextFloat(rng) - 0.5) * 26,
            2.4 + nextFloat(rng) * 1.6,
          );
        }
        flowers.fill(FLOWERS[i % FLOWERS.length] ?? PATH_COLORS.firefly);
      }
    },
    style(): void {
      // Always at dusk: drawn in its own colours.
    },
    destroy(): void {
      root.destroy({ children: true });
    },
  };
}

export function createPath(ctx: RenderContext): Family {
  const { textures } = ctx;
  const root = new Container();
  const trees = new Graphics();
  const walkway = new Graphics();
  const strings = new Graphics();
  const gate = new Graphics();
  const cores = new Graphics();
  const extras = new Container();
  root.addChild(trees, walkway, extras, cores);
  ctx.layers.decor.addChild(root);
  const overhead = new Container();
  const roofLayer = new Container();
  overhead.addChild(roofLayer, strings, gate);
  ctx.layers.canopy.addChild(overhead);
  let roofs: Roof[] = [];
  let fadedAt = 0;
  const glows = new Container();
  const ribbon = new Container();
  glows.addChild(ribbon);
  ctx.layers.glow.addChild(glows);
  let halos: Sprite[] = [];
  let wake: Wake | undefined;
  let wokenAt = 0;
  let bulbs: Sprite[] = [];
  let drawnKey = '';

  const glowAt = (x: number, y: number, radius: number, tint: number = PATH_COLORS.glow) => {
    const glow = new Sprite(textures.halo.texture);
    glow.anchor.set(0.5);
    glow.position.set(x, y);
    glow.scale.set(radius / textures.halo.radius);
    glow.tint = tint;
    return glows.addChild(glow);
  };

  function drawAlleyExtras(way: Way, shown: readonly AlleyExtra[], colors: StandColors) {
    const lit: Sprite[] = [];
    for (const extra of shown) {
      const place = alleyPlace(extra, shown);
      if (place === undefined) {
        continue;
      }
      const { spot } = footingOf(way, place);
      const turn = towardTrail(way, place.side);
      const standing = (parent: Container) => {
        const drawing = parent.addChild(new Graphics());
        drawing.position.set(spot.x, spot.y);
        drawing.rotation = turn;
        return drawing;
      };
      const g = standing(extras);
      const over = standing(roofLayer);
      const [cos, sin] = [Math.cos(turn), Math.sin(turn)];
      const lights = ALLEY_DRAWINGS[extra](g, over, colors);
      const { minX, minY, maxX, maxY } = over.getLocalBounds();
      if (maxX > minX) {
        roofs.push({ drawing: over, box: { minX, minY, maxX, maxY } });
      }
      for (const light of lights) {
        const x = spot.x + light.x * cos - light.y * sin;
        const y = spot.y + light.x * sin + light.y * cos;
        lit.push(glowAt(x, y, EXTRA_GLOW));
      }
    }
    return lit;
  }

  function draw(
    way: Way,
    heading: 1 | -1,
    ahead: DecorId,
    look: 'alley' | 'trail',
    rank: number,
    seed: number,
  ): void {
    const rng = seedRng((seed ^ 0x9e3779b9) >>> 0);
    wake = undefined;
    for (const bulb of bulbs) {
      bulb.destroy();
    }
    for (const child of [...extras.removeChildren(), ...roofLayer.removeChildren()]) {
      child.destroy();
    }
    roofs = [];
    trees.clear();
    walkway.clear();
    strings.clear();
    const lights = gateLights(ahead);
    if (look === 'alley') {
      drawTreeLine(trees, way, -1, rng);
      drawTreeLine(trees, way, 1, rng);
      drawPlanks(walkway, way);
      bulbs = drawBunting(strings, way, lights).map(({ x, y }) => glowAt(x, y, BULB_GLOW));
      const shown = alleyExtras(seed, rank);
      const colors = { cloth: lights[2] ?? PATH_COLORS.pole, trim: lights[0] ?? PATH_COLORS.brass };
      bulbs.push(...drawAlleyExtras(way, shown, colors));
    } else {
      bulbs = drawShores(trees, way, rng).map(({ x, y }) => glowAt(x, y, LAKE_FLY_GLOW));
      drawDirt(walkway, way, rng);
      const lamp = lights[0] ?? PATH_COLORS.glow;
      bulbs.push(
        ...drawStoneLanterns(strings, way).map(({ x, y }) => glowAt(x, y, LAMP_GLOW, lamp)),
      );
      const shown = trailExtras(seed, rank);
      const g = extras.addChild(new Graphics());
      for (const extra of shown) {
        bulbs.push(
          ...TRAIL_DRAWINGS[extra](g, way, lights, rng).map(({ x, y }) => glowAt(x, y, BULB_GLOW)),
        );
      }
    }
    const spot = gateSpot(way, heading);
    drawGate(gate.clear(), ahead, GATE.radius);
    gate.position.set(spot.x, spot.y);
    gate.rotation = turnOf(way, heading);
  }

  return {
    update(state: SimState, _alpha: number, frame: Frame): void {
      root.visible = glows.visible = overhead.visible = frame.set.decor === 'chemin';
      if (!root.visible) {
        if (drawnKey !== '') {
          drawnKey = '';
          releaseDrawing(root);
          releaseDrawing(glows);
          releaseDrawing(overhead);
        }
        return;
      }
      const way = wayOf(state.arena);
      const heading = headingOf(state.festival?.taken?.side);
      const ahead = frame.upcoming?.decor ?? 'lake';
      const festival = state.festival;
      const rank = festival?.scenes.length ?? 0;
      const look = pathLook(
        festival?.scenes.at(-1)?.setId,
        festival?.taken?.setId ?? frame.upcoming?.id,
      );
      const key = [frame.set.id, state.seed, heading, ahead, look, rank].join(':');
      if (key !== drawnKey) {
        drawnKey = key;
        draw(way, heading, ahead, look, rank, state.seed);
      }
      const { light, calm, now } = frame;
      bulbs.forEach((glow, index) => {
        const flicker = calm ? 1 : 0.85 + 0.15 * Math.sin(now * 0.11 + index * 1.7);
        glow.alpha = light.haloAlpha * flicker * 0.7;
      });
      fadeRoofs(state, now, frame.tempo.ticksPerBeat);
      wakeFireflies(state, way, heading, frame);
    },
    reset(): void {
      drawnKey = '';
    },
    destroy(): void {
      root.destroy({ children: true });
      glows.destroy({ children: true });
      overhead.destroy({ children: true });
    },
  };

  function wakeFireflies(state: SimState, way: Way, heading: 1 | -1, frame: Frame): void {
    const { light, calm, now, tempo } = frame;
    const lead =
      heading *
      state.players.reduce(
        (best, player) =>
          player.gone === true ? best : Math.max(best, heading * alongOf(way, player)),
        Number.NEGATIVE_INFINITY,
      );
    if (wake === undefined) {
      wake = createWake(way, lead, heading, state.seed);
      for (const halo of ribbon.removeChildren()) {
        halo.destroy();
      }
      halos = wake.flies.map(() => {
        const halo = new Sprite(textures.halo.texture);
        halo.anchor.set(0.5);
        halo.tint = PATH_COLORS.glow;
        return ribbon.addChild(halo);
      });
      wokenAt = now;
    }
    stepWake(wake, lead, clamp(now - wokenAt, 0, MAX_WAKE_STEP_TICKS) / TICK_RATE_HZ);
    wokenAt = now;
    const beat = frac((now - tempo.originTick) / tempo.ticksPerBeat);
    const flies = wakeFlies(wake, now / TICK_RATE_HZ, beat, calm);
    cores.clear();
    flies.forEach((fly, index) => {
      const halo = halos[index];
      if (halo !== undefined) {
        halo.position.set(fly.x, fly.y);
        halo.scale.set((fly.size * FLY_GLOW) / textures.halo.radius);
        halo.alpha = Math.min(1, fly.alpha) * light.haloAlpha;
      }
      cores.circle(fly.x, fly.y, fly.size).fill({
        color: PATH_COLORS.firefly,
        alpha: Math.min(1, fly.alpha),
      });
    });
  }

  function fadeRoofs(state: SimState, now: number, ticksPerBeat: number): void {
    const focus = ctx.options.focus;
    const watched = state.players.filter((player) =>
      focus?.kind === 'everyone' ? player.gone !== true : player.id === (focus?.playerId ?? 0),
    );
    const elapsed = clamp(now - fadedAt, 0, ticksPerBeat);
    fadedAt = now;
    const step = ((1 - UNDER_ALPHA) * elapsed) / (ticksPerBeat / 4);
    for (const roof of roofs) {
      const target = watched.some((player) => covers(roof, player)) ? UNDER_ALPHA : 1;
      const alpha = roof.drawing.alpha;
      roof.drawing.alpha =
        target < alpha ? Math.max(target, alpha - step) : Math.min(target, alpha + step);
    }
  }
}
